import { get } from './graphql-utils';
import { getEmployee as getEmployeeQuery } from '../graphql/queries';
import { GetEmployeeQuery } from '../API';

export interface CaseRecord {
  caseType?: string;
  employeeId: string;
  employeeName: string;
  employeeNumber?: string;
  caseNumber: string;
  lineManager?: string;
  caseManager?: string;
  department?: string;
  location?: string;
  province?: string;
  race?: string;
  gender?: string;
  personWithDisability?: string;
  dateCaseInitiated?: string;
  counselling?: string;
  pip?: string;
  extendedPip?: string;
  successfulPip?: string;
  incapacityHearing?: string;
  performanceShortfalls?: string;
  investigationConducted?: string;
  employeeSuspended?: string;
  transgressionCategory?: string;
  transgression?: string;
  frequency?: string;
  disciplinaryDiscussion?: string;
  sanction?: string;
  sanctionIssuedDate?: string;
  disciplinaryHearing?: string;
  sanction2?: string;
  sanctionIssuedDate2?: string;
  charges?: string;
  appealProcess?: string;
  appealOutcome?: string;
  caseClosed?: string;
  dateCaseClosed?: string;
  disputeReferred?: string;
  disputeOutcome?: string;

  [key: string]: any;
}

export const COMBINED_CSV_COLUMNS = [
  { label: 'Case Type', getter: o => o.caseType || '' },
  { label: 'Employee Name', getter: o => o.employeeName || '' },
  { label: 'Employee Number', getter: o => o.employeeNumber || '' },
  { label: 'Case Number', getter: o => o.caseNumber || '' },
  { label: 'Line Manager', getter: o => o.lineManager || '' },
  { label: 'Case Manager', getter: o => o.caseManager || '' },
  { label: 'Department', getter: o => o.department || '' },
  { label: 'Location', getter: o => o.location || '' },
  { label: 'Province', getter: o => o.province || '' },
  { label: 'Race', getter: o => o.race || '' },
  { label: 'Gender', getter: o => o.gender || '' },
  { label: 'Person with a disability', getter: o => o.personWithDisability || '' },
  { label: 'Date case initiated', getter: o => o.dateCaseInitiated || '' },
  { label: 'Counselling (Y/N)', getter: o => o.counselling || '' },
  { label: 'PIP (Y/N)', getter: o => o.pip || '' },
  { label: 'Extended PIP (Y/N)', getter: o => o.extendedPip || '' },
  { label: 'PIP / Extended PIP successful (Y/N)', getter: o => o.successfulPip || '' },
  { label: 'Incapacity hearing (Y/N)', getter: o => o.incapacityHearing || '' },
  { label: 'Performance shortfalls', getter: o => o.performanceShortfalls || '' },
  { label: 'Investigation conducted (Y/N)', getter: o => o.investigationConducted || '' },
  { label: 'Employee Suspended (Y/N)', getter: o => o.employeeSuspended || '' },
  { label: 'Transgression category', getter: o => o.transgressionCategory || '' },
  { label: 'Transgression', getter: o => o.transgression || '' },
  { label: 'Frequency', getter: o => o.frequency || '' },
  { label: 'Disciplinary discussion (Y/N)', getter: o => o.disciplinaryDiscussion || '' },
  { label: 'Sanction / Outcome', getter: o => o.sanction || '' },
  { label: 'Sanction / Outcome issued - Date', getter: o => o.sanctionIssuedDate || '' },
  { label: 'Disciplinary hearing (Y/N)', getter: o => o.disciplinaryHearing || '' },
  { label: 'Sanction / Outcome', getter: o => o.sanction2 || '' },
  { label: 'Sanction / Outcome issued - Date', getter: o => o.sanctionIssuedDate2 || '' },
  { label: 'Charges', getter: o => o.charges || '' },
  { label: 'Appeal process (Y/N)', getter: o => o.appealProcess || '' },
  { label: 'Appeal outcome', getter: o => o.appealOutcome || '' },
  { label: 'Case closed', getter: o => o.caseClosed || '' },
  { label: 'Date Case closed', getter: o => o.dateCaseClosed || '' },
  { label: 'Dispute referred (Y/N)', getter: o => o.disputeReferred || '' },
  { label: 'Dispute outcome', getter: o => o.disputeOutcome || '' },
];

export async function buildAndDownloadCaseCSV(
  raw:
    | Array<{
        id: string;
        caseType: 'MISCONDUCT' | 'POOR_PERFORMANCE';
        employeeId: string;
        organisationId?: string;
        masterProcessVars?: Record<string, any>;
        masterProcessInstance?: { startTime?: string };
        expiryDate?: string;
        isCaseClosed?: boolean;
        frequency?: string;
        caseData?: any;
        [key: string]: any;
      }>
    | undefined,
): Promise<void> {
  if (!raw?.length) return;

  const recordPromises = raw.map(async item => {
    const rec: any = {
      employeeId: item.employeeId,
      caseType: item.caseType,
    };
    const vars = item.masterProcessVars || {};

    // basic fields
    rec.caseNumber = vars.caseNumber || item.caseNumber || '';
    rec.dateCaseInitiated = item.masterProcessInstance?.startTime || vars.startTime || '';
    rec.caseClosed = item.isCaseClosed ? 'Yes' : '';
    rec.dateCaseClosed = item.expiryDate !== 'None' ? item.expiryDate || '' : '';
    rec.counselling = vars.counsellingSubflowKey ? 'Yes' : 'No';
    rec.pip = vars.pipSubflowKey ? 'Yes' : 'No';
    rec.extendedPip = vars.extendedPipSubflowKey ? 'Yes' : 'No';
    rec.successfulPip = item.caseData?.hasCompletedFinalWrittenWarningPIP ? 'Yes' : 'No';
    rec.incapacityHearing = vars.hearingSubflowKey ? 'Yes' : 'No';
    rec.investigationConducted = item.caseData?.investigationReportDocuments?.length ? 'Yes' : 'No';
    rec.employeeSuspended = item.caseData?.shouldSuspendPendingInvestigation ? 'Yes' : 'No';
    rec.disputeReferred = item.caseData?.isDisputeReferred ? 'Yes' : 'No';
    rec.disputeOutcome = item.caseData?.disputeOutcome || '';

    // defaults for “second” fields
    rec.sanction2 = '';
    rec.sanctionIssuedDate2 = '';
    rec.charges = '';
    rec.appealProcess = '';
    rec.appealOutcome = '';

    if (item.caseType === 'POOR_PERFORMANCE') {
      // performance shortfalls (array)
      try {
        const arr = JSON.parse(vars.performanceShortfalls || '[]');
        if (Array.isArray(arr)) {
          rec.performanceShortfalls = arr
            .map((a: any) => a.shortfallDescription)
            .filter(Boolean)
            .join('; ');
          // if you happen to store a “second” entry for appeal:
          if (arr[1]) {
            rec.sanction2 = arr[1].potentialSanction || '';
            rec.sanctionIssuedDate2 = arr[1].sanctionIssuedDate || '';
            rec.charges = arr[1].charges || '';
            rec.appealProcess = arr[1].appealProcess ? 'Yes' : 'No';
            rec.appealOutcome = arr[1].appealOutcome || '';
          }
        }
      } catch {
        /* ignore parse errors */
      }
      rec.frequency = item.frequency || vars.frequency || '';
      rec.sanction = vars.sanction || '';
      rec.disciplinaryDiscussion = 'Yes';
      rec.sanctionIssuedDate = vars.sanctionIssuedDate || '';
      rec.disciplinaryHearing = '';
    } else {
      // MISCONDUCT → parse incidents
      try {
        const incArr = JSON.parse(vars.incidents || '[]');
        if (Array.isArray(incArr) && incArr.length) {
          const inc = incArr[0];
          rec.transgressionCategory = inc.transgressionCategory || '';
          rec.transgression = inc.transgression || '';
          rec.frequency = inc.frequency || '';
          rec.sanction = inc.potentialSanction || '';
          rec.sanctionIssuedDate = inc.sanctionIssuedDate || '';
          rec.charges = inc.charges || '';
          rec.appealProcess = inc.appealProcess ? 'Yes' : 'No';
          rec.appealOutcome = inc.appealOutcome || '';
          rec.disciplinaryDiscussion = inc.process === 'DISCUSSION' ? 'Yes' : 'No';
          rec.disciplinaryHearing = inc.process === 'HEARING' ? 'Yes' : 'No';

          // second incident → appeal outcome
          if (incArr[1]) {
            const app = incArr[1];
            rec.sanction2 = app.potentialSanction || '';
            rec.sanctionIssuedDate2 = app.sanctionIssuedDate || '';
          }
        }
      } catch {
        /* ignore parse errors */
      }
    }

    // fetch employee
    try {
      const empResp = await get<GetEmployeeQuery>(getEmployeeQuery, item.employeeId);
      const e = empResp.data?.getEmployee;
      if (e) {
        rec.employeeName = [e.firstName, e.lastName].filter(Boolean).join(' ');
        rec.employeeNumber = e.employeeNumber || '';
        rec.race = e.race || '';
        rec.gender = e.gender || '';
        rec.personWithDisability = e.hasDisability ? 'Yes' : 'No';
        rec.department = e.department?.name || '';
        rec.location = e.physicalAddresses?.[0]?.lineOne || '';
        rec.province = e.physicalAddresses?.[0]?.province || '';
        rec.lineManager = e.directManager ? `${e.directManager.firstName} ${e.directManager.lastName}` : '';
      }
    } catch (err) {
      console.warn('Employee fetch failed', err);
    }
    return rec as CaseRecord;
  });

  let fullRecords = await Promise.all(recordPromises);

  // ←— SORT: misconduct first, then PP; within each by descending caseNumber
  fullRecords = fullRecords.sort((a, b) => {
    if (a.caseType !== b.caseType) {
      return a.caseType === 'MISCONDUCT' ? -1 : 1;
    }
    const aNum = parseInt(a.caseNumber, 10) || 0;
    const bNum = parseInt(b.caseNumber, 10) || 0;
    return bNum - aNum;
  });

  // build and download CSV
  const header = COMBINED_CSV_COLUMNS.map(c => `"${c.label.replace(/"/g, '""')}"`).join(',');
  const rows = fullRecords.map(r =>
    COMBINED_CSV_COLUMNS.map(c => `"${String(c.getter(r) || '').replace(/"/g, '""')}"`).join(','),
  );
  const csv = [header, ...rows].join('\r\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `cases_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
